// Rechenregeln der Auswertungen – reine Funktionen für Server UND Oberfläche, jede mit einer Falltabelle im Test.

export type AbcClass = 'A' | 'B' | 'C'

export interface AbcThresholds {
  /** kumulierter Anteil in Basispunkten, bis zu dem Klasse A reicht (Standard 80 %) */
  a: number
  /** … bis zu dem Klasse B reicht (Standard 95 %) */
  b: number
}

export const ABC_DEFAULT: AbcThresholds = { a: 8000, b: 9500 }

export interface AbcResult<T> {
  row: T
  /** Rang nach Wert, 1 = größter */
  rank: number
  abc: AbcClass
  /** Anteil am Ganzen in Basispunkten */
  shareBp: number
  /** kumulierter Anteil einschließlich dieser Zeile */
  cumulativeBp: number
}

/**
 * ABC-Analyse: Zeilen nach Wert absteigend; A sind die Zeilen, bis der kumulierte Anteil die A-Grenze erreicht hat –
 * die Zeile, die die Grenze überschreitet, gehört noch dazu. B entsprechend bis zur B-Grenze, der Rest ist C.
 * Zeilen ohne positiven Wert sind immer C (sie tragen nichts bei). Bei Gleichstand bleibt die Reihenfolge der Eingabe.
 */
export function classifyAbc<T>(rows: readonly T[], valueOf: (row: T) => number, thresholds: AbcThresholds = ABC_DEFAULT): AbcResult<T>[] {
  const sorted = rows.map((row, index) => ({ row, index, value: valueOf(row) })).sort((x, y) => y.value - x.value || x.index - y.index)
  const total = sorted.reduce((sum, entry) => sum + Math.max(entry.value, 0), 0)
  let before = 0
  return sorted.map((entry, position) => {
    const value = Math.max(entry.value, 0)
    const beforeBp = total === 0 ? 10_000 : Math.round((before * 10_000) / total)
    before += value
    const cumulativeBp = total === 0 ? 0 : Math.round((before * 10_000) / total)
    const abc: AbcClass = value <= 0 ? 'C' : beforeBp < thresholds.a ? 'A' : beforeBp < thresholds.b ? 'B' : 'C'
    return { row: entry.row, rank: position + 1, abc, shareBp: total === 0 ? 0 : Math.round((value * 10_000) / total), cumulativeBp }
  })
}

/**
 * Reichweite in ganzen Tagen: Bestand ÷ durchschnittlicher Tagesabgang des Zeitraums (abgerundet – ein angebrochener
 * Tag reicht nicht mehr). null = kein Abgang im Zeitraum (keine Aussage möglich); 0 = nichts mehr da.
 */
export function daysOfSupply(onHandQty: number, issuedQty: number, days: number): number | null {
  if (days <= 0 || issuedQty <= 0) return null
  if (onHandQty <= 0) return 0
  return Math.floor((onHandQty * days) / issuedQty)
}

/**
 * Lagerumschlag aufs Jahr gerechnet, in Hundertsteln (1250 = 12,5-mal im Jahr): Wareneinsatz des Zeitraums ÷
 * durchschnittlicher Lagerwert × 365 ÷ Tage. null ohne Lagerwert.
 */
export function turnoverRate(costCents: number, averageStockValueCents: number, days: number): number | null {
  if (days <= 0 || averageStockValueCents <= 0) return null
  return Math.round((costCents * 365 * 100) / (averageStockValueCents * days))
}

/** Rohertrag in Basispunkten des Umsatzes; null ohne Umsatz. */
export function marginBp(revenueCents: number, costCents: number): number | null {
  if (revenueCents === 0) return null
  return Math.round(((revenueCents - costCents) * 10_000) / revenueCents)
}

/** Altersklassen offener Posten nach Tagen seit Fälligkeit. */
export const AGE_BUCKETS = ['not_due', 'd1_30', 'd31_60', 'd61_90', 'd90_plus'] as const
export type AgeBucket = (typeof AGE_BUCKETS)[number]
export const AGE_BUCKET_LABELS: Record<AgeBucket, string> = {
  not_due: 'Nicht fällig',
  d1_30: '1–30 Tage',
  d31_60: '31–60 Tage',
  d61_90: '61–90 Tage',
  d90_plus: 'über 90 Tage',
}

/** Tage überfällig (≤ 0 = noch nicht fällig bzw. heute fällig) → Altersklasse. */
export function ageBucket(daysOverdue: number): AgeBucket {
  if (daysOverdue <= 0) return 'not_due'
  if (daysOverdue <= 30) return 'd1_30'
  if (daysOverdue <= 60) return 'd31_60'
  if (daysOverdue <= 90) return 'd61_90'
  return 'd90_plus'
}

/**
 * Forderungslaufzeit in Tagen (DSO): offene Forderungen ÷ Umsatz des Zeitraums × Tage, auf ganze Tage gerundet.
 * null ohne Umsatz.
 */
export function daysSalesOutstanding(receivablesCents: number, revenueGrossCents: number, days: number): number | null {
  if (revenueGrossCents <= 0 || days <= 0) return null
  return Math.round((receivablesCents * days) / revenueGrossCents)
}
